import { Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { QaiSubtitlesFormatting } from '@qai/ui';
import { ControlService } from '../../library/control.service';
import { RealtimeService } from '../../library/realtime/realtime.service';
import { UserVideoService } from '../../library/user-video.service';
import { DeviceSettingsService } from '../../library/device-settings.service';
import { PlayerService } from '../../library/player.service';
import { CallStoreService } from '../../library/call-store/call-store.service';
import { SubtitleKind } from 'src/app/utils/enums/learner/subtitle-kind.enum';
import { PathwaysService } from 'src/app/services/learner/pathways.service';

@Component({
  host: {
    class: 'theme-dark',
  },
  selector: 'app-assessment-call',
  standalone: false,
  templateUrl: './assessment-call.component.html',
  styleUrls: ['./assessment-call.component.scss'],
  providers: [
    { provide: TRANSLOCO_SCOPE, useValue: 'learningPathway' },
    { provide: TRANSLOCO_SCOPE, useValue: 'call' },
  ],
})
export class AssessmentCallComponent {
  private readonly pathwaysService = inject(PathwaysService);
  private readonly destroyRef = inject(DestroyRef);

  readonly controlService = input<ControlService | undefined>(undefined);
  readonly realtimeService = input<RealtimeService | undefined>(undefined);
  readonly userVideoService = input<UserVideoService | undefined>(undefined);
  readonly deviceSettingsService = input<DeviceSettingsService | undefined>(undefined);
  readonly playerService = input<PlayerService | undefined>(undefined);
  readonly callStore = input<CallStoreService | undefined>(undefined);
  readonly enrollmentUuid = input.required<string>();
  readonly title = input('');
  readonly captionText = input('');
  readonly subtitleKind = input<SubtitleKind>(SubtitleKind.Ready);
  readonly subtitleText = input('');
  readonly subtitleFormatting = input<QaiSubtitlesFormatting>('italic');
  readonly avatarPhotoUrl = input('');
  readonly showAvatarPhoto = input(false);
  readonly mainIsLoading = input(false);
  readonly disableConfirmActionButton = input(false);

  readonly settingsClicked = output<void>();
  readonly completeStep = output<void>();
  readonly leaveStep = output<void>();

  readonly assessmentExitModalOpen = signal(false);
  readonly isCheckingReadiness = signal(false);
  private assessmentExitFromNextStep = false;

  protected get disableMicButton(): boolean {
    return !!this.realtimeService()?.isFirstMessage;
  }

  protected get disableVideoButton(): boolean {
    return !this.callStore()?.getCall();
  }

  protected getCameraAvailable(): boolean {
    return !this.deviceSettingsService()?.isCameraUnavailable;
  }

  handleSettingsClick(): void {
    this.settingsClicked.emit();
  }

  handleLeaveStepClick(): void {
    this.assessmentExitFromNextStep = false;
    this.pauseCall();
    this.assessmentExitModalOpen.set(true);
  }

  handleMicrophoneClick(): void {
    this.controlService()?.toggleMic();
  }

  handleVideoClick(): void {
    if (this.deviceSettingsService()?.isCameraUnavailable) {
      this.settingsClicked.emit();
    } else {
      this.controlService()?.toggleVideo();
    }
  }

  handleConfirmActionClick(): void {
    this.isCheckingReadiness.set(true);

    this.pathwaysService.getAssessmentReadiness(this.enrollmentUuid())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (isReady) => {
          this.isCheckingReadiness.set(false);
          if (isReady) {
            this.completeStep.emit();
          } else {
            this.pauseCall();
            this.assessmentExitFromNextStep = true;
            this.assessmentExitModalOpen.set(true);
          }
        },
        error: () => {
          this.isCheckingReadiness.set(false);
          this.pauseCall();
          this.assessmentExitFromNextStep = true;
          this.assessmentExitModalOpen.set(true);
        },
      });
  }

  handleAssessmentExitResume(): void {
    this.assessmentExitModalOpen.set(false);
    this.unpauseCall();
  }

  handleAssessmentExitConfirm(): void {
    this.assessmentExitModalOpen.set(false);
    if (this.assessmentExitFromNextStep) {
      this.completeStep.emit();
    } else {
      this.leaveStep.emit();
    }
  }

  private pauseCall(): void {
    if (!this.callStore()?.getIsPaused()) {
      this.controlService()?.pauseCall();
    }
  }

  private unpauseCall(): void {
    if (this.callStore()?.getIsPaused()) {
      this.controlService()?.unpauseCall();
    }
  }
}
